import { useCallback, useMemo } from "react";

import { useTypedSelector } from "../../redux/store";
import { useTaxiServiceFilterAccessControl, useCompanyIdsDecoder } from "..";

/**
 * Декодирует companyIds в набор taxiServiceIds (филиалов)
 * companyIds может быть number[] или ["all"]
 */
function useTaxiServiceIdsByCompanyIdsDecoder() {
	const { accessTaxiServicesIds } = useTaxiServiceFilterAccessControl<any>();
	const { models: taxiServices } = useTypedSelector(
		(state) => state.taxiServices,
	);

	const decodeCompanyIds = useCompanyIdsDecoder();

	const availableTaxiServiceIds = useMemo(
		() => taxiServices.map((t) => t.id),
		[taxiServices],
	);

	const isAllIds = useCallback(
		(
			ids: useTaxiServiceIdsByCompanyIdsDecoder.EncodedIds,
		): ids is ["all"] => Array.isArray(ids) && ids[0] === "all",
		[],
	);

	const decoder = useCallback(
		(
			companyIds: useTaxiServiceIdsByCompanyIdsDecoder.EncodedIds,
			options: useTaxiServiceIdsByCompanyIdsDecoder.DecoderOptions = {},
		): number[] => {
			const baseTaxiServices = options.isOnlyAccess
				? accessTaxiServicesIds
				: availableTaxiServiceIds;

			const decodedCompanyIds = decodeCompanyIds(companyIds, {
				isOnlyAccess: options.isOnlyAccess,
			});

			if (isAllIds(companyIds)) return baseTaxiServices;

			return baseTaxiServices.filter((taxiServiceId) => {
				const taxiService = taxiServices.find(
					(t) => t.id === taxiServiceId,
				);
				return taxiService?.company?.id
					? decodedCompanyIds.includes(taxiService.company.id)
					: false;
			});
		},
		[
			accessTaxiServicesIds,
			availableTaxiServiceIds,
			decodeCompanyIds,
			isAllIds,
			taxiServices,
		],
	);

	return decoder;
}

declare namespace useTaxiServiceIdsByCompanyIdsDecoder {
	export type EncodedIds = number[] | ["all"];

	export interface DecoderOptions {
		isOnlyAccess?: boolean;
	}
}

export default useTaxiServiceIdsByCompanyIdsDecoder;
